import { useState, type FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { Toggle } from 'components/atoms/Form/Toggle';
import LoadingSpinner from 'components/atoms/LoadingSpinner/LoadingSpinner.component';
import useAccountableBanks from 'hooks/Bank/useAccountableBanks';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { patchUserDangerously } from 'api/v2/users/settings';
import showToast from 'utils/toast';
import { UserCacheKeys } from 'types/cacheKeys.types';
import type { ActivationStepProps } from '../../types';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';

/**
 * Hook to update user IBAN with proper cache invalidation
 * Defined inline since it's only used in this component
 */
const useUpdateUserIBAN = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (iban: string | null) =>
      patchUserDangerously({ IBAN: iban }, { skipWarningChecks: true }),

    onSuccess: () => {
      // Invalidate user cache so useGetCurrentCustomerData gets fresh data
      queryClient.invalidateQueries([UserCacheKeys.UserData]);
    },

    onError: (error) => {
      console.error('Failed to update IBAN:', error);
      showToast({
        title: t('something_went_wrong'),
        variant: 'error',
      });
    },
  });
};

// Language-specific invoice images
import invoiceImageEn from './images/invoice_payment_en@3x.webp';
import invoiceImageDe from './images/invoice_payment_de@3x.webp';
import invoiceImageFr from './images/invoice_payment_fr@3x.webp';
import invoiceImageNl from './images/invoice_payment_nl@3x.webp';

const LANGUAGE_IMAGES: Record<string, string> = {
  en: invoiceImageEn,
  de: invoiceImageDe,
  fr: invoiceImageFr,
  nl: invoiceImageNl,
};

const DEBOUNCE_TIME = 200;
/**
 * Step to enable Accountable IBAN on invoices
 * Full integration with user settings
 * Bookmark step for tracking
 */
const AccIbanOnInvoicesStep: FC<ActivationStepProps> = ({
  onContinue,
  onBack,
  // Wrapper props (automatically injected)
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  isUpdatingStep,
}) => {
  const { t, i18n } = useTranslation();
  const { mainAccountableBank } = useAccountableBanks();
  const { data: user } = useGetCurrentCustomerData();
  const { mutateAsync: updateIBANMutationAsync, isLoading: isUpdating } =
    useUpdateUserIBAN();

  const invoiceImage = LANGUAGE_IMAGES[i18n.language] || LANGUAGE_IMAGES.en;

  const accountableBankIban = mainAccountableBank?.bankAccountReference?.IBAN;
  const currentUserIban = user?.IBAN;

  // Check if Accountable IBAN is already set (from fresh cache data)
  const isAccountableIbanAlreadySet =
    accountableBankIban && currentUserIban === accountableBankIban;

  // Local toggle state - initialized from server state
  const [isEnabled, setIsEnabled] = useState(
    isAccountableIbanAlreadySet || false,
  );
  const [isPendingUpdate, setIsPendingUpdate] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track if auto-enable has been attempted this mount
  const hasAutoEnabledRef = useRef(false);
  const serverStateRef = useRef(isAccountableIbanAlreadySet || false);

  const handleToggle = (newValue: boolean) => {
    // Prevent toggling before IBAN data is loaded
    if (!accountableBankIban) return;

    setIsPendingUpdate(true);
    setIsEnabled(newValue);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      setIsPendingUpdate(false);
      debounceTimerRef.current = null;

      // Skip API if final state matches server state
      if (newValue === serverStateRef.current) {
        return; // No change needed
      }

      const ibanToSet = newValue ? accountableBankIban : null;

      try {
        await updateIBANMutationAsync(ibanToSet);
        serverStateRef.current = newValue; // Update confirmed state
        showToast({
          title: newValue
            ? t('accountable_banking.activation.acc_iban_on_invoices.enabled')
            : t('accountable_banking.activation.acc_iban_on_invoices.disabled'),
          variant: 'success',
        });
      } catch {
        setIsEnabled(!newValue); // Revert UI
      }
    }, DEBOUNCE_TIME);
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Auto-enable toggle on step mount
  // Requirement: Every time user enters this step, automatically enable IBAN
  // Cache invalidation ensures isAccountableIbanAlreadySet is accurate
  useEffect(() => {
    if (!accountableBankIban) return;
    if (hasAutoEnabledRef.current) return; // Only auto-enable once per mount

    hasAutoEnabledRef.current = true;

    // Always show toggle as enabled
    setIsEnabled(true);

    // Only send request if IBAN is not already set to accountable bank
    if (isAccountableIbanAlreadySet) {
      // IBAN already correct - update serverStateRef to reflect actual state
      serverStateRef.current = true;
      return;
    }

    // Send the enable request
    const autoEnable = async () => {
      try {
        await updateIBANMutationAsync(accountableBankIban);
        serverStateRef.current = true; // Update ref after successful auto-enable
        showToast({
          title: t(
            'accountable_banking.activation.acc_iban_on_invoices.auto_enabled',
          ),
          variant: 'success',
        });
      } catch {
        // Revert toggle on error (error toast shown by mutation)
        setIsEnabled(false);
      }
    };

    autoEnable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountableBankIban, isAccountableIbanAlreadySet]);

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      title={title}
      subtitle={subtitle}
      icon={icon}
      showBackButton
      onBack={onBack}
    >
      {/* Invoice illustration */}
      <div
        className="relative flex justify-center items-center mb-8"
        style={{ minHeight: '250px' }}
      >
        {!imageLoaded && (
          <div className="absolute inset-0 flex justify-center items-center">
            <LoadingSpinner size="lg" color="bank" />
          </div>
        )}
        <img
          src={invoiceImage}
          alt="Image above the toggle to enable or disable Accountable IBAN on invoices"
          className={classNames(
            'transition-opacity duration-300 ease-in-out max-w-full h-auto',
            { 'opacity-0': !imageLoaded },
          )}
          style={{ maxHeight: '250px' }}
          onLoad={() => setImageLoaded(true)}
        />
      </div>

      {/* Toggle */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <span className="font-medium text-gray-900">
            {t(
              'accountable_banking.activation.acc_iban_on_invoices.toggle_label',
            )}
          </span>
          <Toggle
            enabled={isEnabled}
            onChange={handleToggle}
            disabled={isUpdating || !accountableBankIban}
          />
        </div>
      </div>

      <Button
        onClick={onContinue}
        className="w-full"
        color="bank"
        disabled={isUpdating || isUpdatingStep || isPendingUpdate}
      >
        {t('continue')}
      </Button>
    </OnboardingStepWrapper>
  );
};

export default AccIbanOnInvoicesStep;
